import { cloneDeep } from "lodash";

import {
  FrequencyIntervalItem,
  PermissionBit,
  QuestionnaireAutomatonSettingsResponse,
} from "../../../api";
import { resolveEnumLabel } from "../../../components/ui-kit/fields/utils";
import {
  monitoringTypes,
  questionnaireTypes,
} from "../../../constants/enum-to-text";
import { AccessRules } from "../../../constants/roles";
import { DataModel } from "../../../models/data-model";
import { formatDuration } from "./duration-utils";

export class QuestionnaireSettings extends DataModel<QuestionnaireSettingsItem> {
  readonly idAttr = "uuid" as keyof QuestionnaireSettingsItem;

  constructor(
    data: QuestionnaireSettingsItem[],
    permissions: PermissionBit[],
    userUUID: string,
  ) {
    super(data, permissions, userUUID);

    this.access = {
      editRules: { medical: AccessRules.none, monitoring: AccessRules.none },
      deleteRules: { medical: AccessRules.none, monitoring: AccessRules.none },
      historyRequiresMonitoring: false,
    };
  }

  prepareDraft(item: any) {
    const field = super.prepareDraft(item);

    if (item.clinic) {
      field["clinic_uuid"] = item.clinic.uuid;
    }
    if (item.physician) {
      field["physician_uuid"] = item.physician.uuid;
    }

    // Deep copy complex fields
    if (item.filling_frequency) {
      field["filling_frequency"] = cloneDeep(item.filling_frequency);
    }
    if (item.first_after_active) {
      field["first_after_active"] = cloneDeep(item.first_after_active);
    }
    if (item.stop_after_time_from_first) {
      field["stop_after_time_from_first"] = cloneDeep(
        item.stop_after_time_from_first,
      );
    }

    return field;
  }
}

export function toQuestionnaireSettingsItem(
  item: QuestionnaireAutomatonSettingsResponse,
): QuestionnaireSettingsItem {
  return {
    ...item,
    questionnaireText:
      questionnaireTypes[item.questionnaire_id] ??
      String(item.questionnaire_id),
    monitoringTypeText:
      resolveEnumLabel(monitoringTypes[item.monitoring_type]) ||
      String(item.monitoring_type),
    clinicName: item.clinic?.name ?? "",
    physicianName: item.physician
      ? `${item.physician.given_name} ${item.physician.family_name}`
      : "",
  };
}

export function stringifyFrequency(freq: FrequencyIntervalItem[]): string {
  if (!freq || freq.length === 0) return "—";
  return freq
    .map((f) => {
      const delta = formatDuration(f.delta);
      // When a count is shown, keep a "—" placeholder for an empty delta
      // (e.g. "3x —"); the bare count alone would read as incomplete. Without a
      // count we render nothing, staying in sync with the other tables.
      return f.count != null ? `${f.count}x ${delta || "—"}` : delta;
    })
    .join(", ");
}

export type QuestionnaireSettingsItem =
  QuestionnaireAutomatonSettingsResponse & {
    questionnaireText: string;
    monitoringTypeText: string;
    clinicName: string;
    physicianName: string;
  };
